import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_IOTHUB } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import './AddDevice.css';
import { Tile, TooltipIcon, UploadTile } from '../UI-Elements';

const STATIC_TILES = [
  { idSuffix: 'template', icon: '📝', label: 'Endgerät über Gerätetyp registrieren', buttonText: 'Registrierung starten', tooltip: 'Wähle einen Gerätetyp aus der Datenbank aus. Die Keys müssen anschließend manuell eingetragen werden.' },
  { idSuffix: 'manual',   icon: '🔌', label: 'Endgerät manuell registrieren', buttonText: 'Registrierung starten', tooltip: 'Diese Option ermöglicht dir, sämtliche Gerätedaten und Converter vollständig selbst zu definieren. Sie eignet sich für Geräte, die nicht in der Datenbank vorhanden sind.' },
];

const AddDevice = () => {
  const navigate = useNavigate();
  const { iotHubToken } = useAuth();
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadError, setBulkUploadError] = useState('');

  const handleImageUpload = async (file) => {
    setImageError('');
    setImageUploading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 40000);
    try {
      const formData = new FormData();
      formData.append('image', file, file.name);

      const res = await fetch(`${API_BASE_IOTHUB}/api/v1/devices/scan`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${iotHubToken}` },
        body: formData,
        signal: controller.signal,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setImageError(errData.detail || errData.message || 'Analyse fehlgeschlagen. Bitte erneut versuchen.');
        return;
      }

      const data = await res.json();
      navigate('/register-device-type', {
        state: { devEUI: data.dev_eui ?? '', appKey: data.app_key ?? '', scanStatus: data.status ?? '' },
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        setImageError('DevEUI und AppKey konnten nicht erkannt werden.');
      } else {
        setImageError(err.message || 'Netzwerkfehler. Bitte erneut versuchen.');
      }
    } finally {
      clearTimeout(timeoutId);
      setImageUploading(false);
    }
  };

  const handleBulkUpload = async (file) => {
    setBulkUploadError('');
    setBulkUploading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 40000);

    try {
      const formData = new FormData();
      formData.append('file', file, file.name);

      const res = await fetch(`${API_BASE_IOTHUB}/api/v1/devices/bulk-upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${iotHubToken}` },
        body: formData,
        signal: controller.signal,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setBulkUploadError(errData.detail || errData.message || 'Upload fehlgeschlagen. Bitte erneut versuchen.');
        return;
      }

      const data = await res.json().catch(() => ({}));

      navigate('/overview-added-devices', {
        state: {
          bulkUploadResult: {
            file_name: data.file_name || data.filename || file.name,
            total_rows: data.total_rows ?? 0,
            successful_count: data.successful_count ?? 0,
            failed_count: data.failed_count ?? 0,
            failed: Array.isArray(data.failed) ? data.failed : [],
          },
        },
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        setBulkUploadError('Der Upload hat zu lange gedauert. Bitte erneut versuchen.');
      } else {
        setBulkUploadError(err.message || 'Netzwerkfehler. Bitte erneut versuchen.');
      }
    } finally {
      clearTimeout(timeoutId);
      setBulkUploading(false);
    }
  };

  return (
    <div className="home-container add-device">
      <div className="device-toolbar">
        <div className="device-toolbar-left">
          <h1 className="device-title">Neues Endgerät hinzufügen</h1>
        </div>
      </div>

      <div className="cards-container-large">
        {/* Bild-Kachel */}
        <div className="card-wrapper-large">
          <div className="card-label-large">
            <span className="card-label-text">DevEUI und AppKey über ein Bild auslesen</span>
            <TooltipIcon idSuffix="img" text="Registriere dein Gerät schneller per Foto. Die Plattform erkennt DevEUI und App‑Key automatisch, ohne manuelle Eingaben. Achte darauf, dass beide Angaben auf dem Foto gut lesbar sind. Bei unscharfen oder schlecht ausgeleuchteten Bildern kann die Erkennung fehlschlagen. Unterstützte Formate: PNG und JPEG/JPG." />
          </div>
          <UploadTile
            accept=".png,.jpg,.jpeg"
            icon="📦"
            selectLabel="Bild auswählen"
            changeLabel="Andere Datei auswählen"
            onUpload={handleImageUpload}
            uploading={imageUploading}
          />
          {imageError && (
            <p className="adddevice-upload-error" role="alert">{imageError}</p>
          )}
        </div>

        {/* CSV-Kachel */}
        <div className="card-wrapper-large">
          <div className="card-label-large">
            <span className="card-label-text">Mehrere Endgeräte über eine Datei registrieren</span>
            <TooltipIcon idSuffix="file" text="Registriere mehrere Geräte schnell per CSV- oder XLSX-Datei. Die Plattform liest alle relevanten Daten und Keys direkt aus der hochgeladenen Datei aus, sodass keine manuelle Eingabe erforderlich ist. Erforderliche Spalten: Name, DevEUI, AppKey, Vendor und Model." />
          </div>
          <UploadTile
            accept=".csv,.xlsx"
            icon="📄"
            selectLabel="Datei auswählen"
            changeLabel="Andere Datei auswählen"
            onUpload={handleBulkUpload}
            uploading={bulkUploading}
          />
          {bulkUploadError && (
            <p className="adddevice-upload-error" role="alert">{bulkUploadError}</p>
          )}
        </div>

        {/* Restliche Kacheln */}
        {STATIC_TILES.map(({ idSuffix, icon, label, buttonText, tooltip }) => (
          <div key={idSuffix} className="card-wrapper-large">
            <div className="card-label-large">
              <span className="card-label-text">{label}</span>
              <TooltipIcon idSuffix={idSuffix} text={tooltip} />
            </div>
            <Tile
              icon={icon}
              buttonText={buttonText}
              onClick={idSuffix === 'template' ? () => navigate('/register-device-type') : undefined}
              disabled={idSuffix === 'manual'}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default AddDevice;
